import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import PropTypes from "prop-types";
import Form from "@accredo/fe-shared/components/Form/Form.jsx";
import FormTextField from "../../../components/Form/controls/FormTextField.jsx";
import FormSwitch from "../../../components/Form/controls/FormSwitch.jsx";
import SubmitButton from "@accredo/fe-shared/components/Form/controls/SubmitButton.jsx";
import DocumentTypeIconPicker from "./DocumentTypeIconPicker.jsx";
import { DOCUMENT_TYPE_ICON_OPTIONS } from "./documentTypeIconOptions.js";

const schema = yup.object({
  slug: yup.string().required("Slug ist erforderlich"),
  label: yup.string().required("Bezeichnung ist erforderlich"),
  icon: yup
    .string()
    .required("Icon ist erforderlich")
    .oneOf(DOCUMENT_TYPE_ICON_OPTIONS.map((option) => option.name)),
  is_active: yup.boolean(),
});

const DEFAULT_VALUES = {
  slug: "",
  label: "",
  icon: "",
  is_active: true,
};

function IconField() {
  const { control } = useFormContext();

  return (
    <Controller
      name="icon"
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Icon
          </Typography>
          <DocumentTypeIconPicker value={field.value} onChange={field.onChange} />
          {error && <FormHelperText error>{error.message}</FormHelperText>}
        </Box>
      )}
    />
  );
}

function DocumentTypeFormModal({
  open,
  onClose,
  documentType,
  assignedCategories,
  unmappedCategories,
  onCreate,
  onUpdate,
  onSyncCategories,
}) {
  const isEdit = Boolean(documentType);
  const { enqueueSnackbar } = useSnackbar();
  const [defaultValues, setDefaultValues] = useState(DEFAULT_VALUES);
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    if (!open) return;
    setDefaultValues(
      isEdit
        ? {
            slug: documentType.slug ?? "",
            label: documentType.label ?? "",
            icon: documentType.icon ?? "",
            is_active: documentType.is_active ?? true,
          }
        : DEFAULT_VALUES
    );
    setSelectedCategories(assignedCategories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentType, isEdit]);

  const categoryOptions = Array.from(
    new Set([...unmappedCategories, ...assignedCategories])
  ).sort();

  async function handleSubmit(data, { setError }) {
    try {
      const document_type_id = isEdit
        ? documentType.id
        : (await onCreate(data))?.data?.id;

      if (isEdit) {
        await onUpdate(documentType.id, data);
      }

      const toAdd = selectedCategories.filter(
        (category) => !assignedCategories.includes(category)
      );
      const toRemove = assignedCategories.filter(
        (category) => !selectedCategories.includes(category)
      );
      if (toAdd.length > 0 || toRemove.length > 0) {
        await onSyncCategories(document_type_id, { toAdd, toRemove });
      }

      enqueueSnackbar(
        isEdit ? "Dokumenttyp gespeichert" : "Dokumenttyp angelegt",
        { variant: "success" }
      );
      onClose(true);
      return true;
    } catch (err) {
      const validation = err?.response?.data?.errors;
      if (validation) {
        Object.entries(validation).forEach(([field, messages]) => {
          setError(field, { message: messages[0] });
        });
      } else {
        enqueueSnackbar("Speichern fehlgeschlagen", { variant: "error" });
      }
    }
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? "Dokumenttyp bearbeiten" : "Neuer Dokumenttyp"}
      </DialogTitle>

      <Form
        key={documentType?.id ?? "new"}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        resolver={yupResolver(schema)}
        mode="onChange"
      >
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormTextField name="label" label="Bezeichnung" required fullWidth />

            <FormTextField
              name="slug"
              label="Slug"
              required
              fullWidth
              helperText="Eindeutiger technischer Bezeichner, z.B. 'rechnungen'"
              inputProps={{ style: { fontFamily: "monospace" } }}
            />

            <IconField />

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Kategorien (KATEGORIE1)
              </Typography>
              <Autocomplete
                multiple
                options={categoryOptions}
                value={selectedCategories}
                onChange={(_, value) => setSelectedCategories(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Kategorie auswählen..."
                  />
                )}
              />
              <FormHelperText>
                Noch nicht zugeordnete Kategorien aus dem Import, mehrere
                gleichzeitig zuweisbar.
              </FormHelperText>
            </Box>

            <FormSwitch name="is_active" label="Aktiv" />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box flexGrow={1} />
          <DsButton variant="tertiary" onClick={() => onClose(false)}>
            Abbrechen
          </DsButton>
          <SubmitButton requireDirty={false}>Speichern</SubmitButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
}

DocumentTypeFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documentType: PropTypes.object,
  assignedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  unmappedCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onSyncCategories: PropTypes.func.isRequired,
};

export default DocumentTypeFormModal;
